import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  getCycleLowFields,
  getCycleHighFields,
  buildSettingKey,
  LEGACY_WEEKLY_CYCLE_LOW_BASE_KEYS,
  LEGACY_WEEKLY_CYCLE_HIGH_BASE_KEYS,
} from './cycleTuningConfig';
import {
  updateSystemSetting,
  deleteSystemSetting,
} from '../../../api/admin';
import useForwardConfidence from './useForwardConfidence';
import { toCanonicalTimeframe } from '../../../utils/timeframes';

// Render the accuracy metric in the panel header strip. Three states:
//   - loading: spinner-y "measuring…"
//   - error: muted red so users know the indicator isn't trustworthy
//   - data: the number, ideally with sample size for context
// We deliberately don't try to be clever about color thresholds yet
// (e.g. green > 70%) — without confidence in what "good" means per
// symbol, premature traffic-lights are worse than no traffic-lights.
const formatAccuracyLabel = ({ data, loading, error }) => {
  if (loading) {return { text: 'measuring…', color: 'text.secondary' };}
  if (error) {return { text: 'accuracy: unavailable', color: 'error.main' };}
  if (!data) {return null;}
  const sides = ['low', 'high'].filter((s) => data[s]);
  if (sides.length === 0) {return null;}
  const parts = sides.map((s) => {
    const sideData = data[s];
    if (sideData?.tooFewSamples) {
      return `${s[0].toUpperCase()}${s.slice(1)}: not enough data (${sideData.sampleSize})`;
    }
    if (sideData?.accuracy === null || sideData?.accuracy === undefined) {
      return `${s[0].toUpperCase()}${s.slice(1)}: pending`;
    }
    const pct = Math.round(sideData.accuracy * 100);
    return `${s[0].toUpperCase()}${s.slice(1)}: ${pct}% (n=${sideData.sampleSize})`;
  });
  return { text: parts.join(' · '), color: 'text.secondary' };
};

/**
 * CycleTuningPanel — live-preview tuner for cycle detection parameters
 * on the current symbol and timeframe.
 *
 * The panel holds a *local* view of the 8 low + 8 high parameters. Whenever
 * the user edits a field the value moves into local state immediately,
 * and the parent passes that local state into the chart's re-detection so
 * markers move in real time. Nothing persists until the user hits Save.
 *
 * The parent owns the persisted settings (loaded from system-settings) and
 * the chart's current timeframe. We receive both via props
 * (`persistedLowSettings` / `persistedHighSettings` / `timeframe`) and emit
 * local-override objects via `onLocalLowChange` / `onLocalHighChange`.
 *
 * Save writes each changed field via the admin API (per-symbol override) at
 * the new-schema key for the current timeframe. On weekly we additionally
 * fire-and-forget delete the legacy key so the library's dual-read can't
 * resurrect a pre-Phase-2b override. Daily and hourly never had a legacy
 * schema so the legacy-delete branch is a no-op there.
 *
 * "Reset to global" deletes every per-symbol override key for the symbol +
 * current timeframe so detection falls back to the global / library default.
 * Both operations refresh the parent's persisted state via
 * `onSettingsChangedRemotely` afterwards.
 */
const CycleTuningPanel = ({
  open,
  onClose,
  symbol,
  timeframe,
  lowLoadGeneration,
  highLoadGeneration,
  persistedLowSettings,
  persistedHighSettings,
  localLowSettings,
  localHighSettings,
  onLocalLowChange,
  onLocalHighChange,
  onSettingsChangedRemotely,
}) => {
  // Resolve to a sane timeframe key even if the prop is missing — the panel
  // shouldn't be rendered without a chart context, but defaulting to weekly
  // matches the pre-Phase-3a behaviour for any forgotten-prop call site.
  const resolvedTimeframe = (timeframe || 'weekly').toLowerCase();
  const isWeekly = resolvedTimeframe === 'weekly';

  // Field arrays are derived per-timeframe so each entry's `baseKey` points
  // at the right new-schema key shape (`indicators.cycleLow.<tf>.<field>`).
  const lowFields = useMemo(() => getCycleLowFields(resolvedTimeframe), [resolvedTimeframe]);
  const highFields = useMemo(() => getCycleHighFields(resolvedTimeframe), [resolvedTimeframe]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [resetting, setResetting] = useState(false);
  // Per-side visibility so users can collapse whichever they're not tuning.
  // Defaults: both shown, but tuning highs and lows together is rare.
  const [showLowsSection, setShowLowsSection] = useState(true);
  const [showHighsSection, setShowHighsSection] = useState(true);

  // Draggable-popover position. Defaults to top-right corner, then user drag
  // updates it. Pointer-based drag; header acts as the drag handle. Child
  // buttons in the header stop propagation so clicking them doesn't start
  // a drag.
  const [position, setPosition] = useState({ x: null, y: 16 });
  const dragStateRef = useRef(null);

  // Stale-persisted detection. The parent's settings loaders are async and
  // fire when timeframe changes, but the new persisted values won't reach
  // us until after the fetches resolve. If we let the user edit during that
  // window, `handleLowFieldChange` would seed local state from the previous
  // TF's persisted values, and on save we'd write those to the new TF as
  // per-symbol overrides — silently corrupting the new TF's settings.
  //
  // We snapshot both load-generation counters at TF change and only mark
  // settings fresh once BOTH have advanced. Watching the counters rather
  // than the persisted refs directly is required because
  // `setWeeklyCycleLowSettings(null)` after a previous null state is a React
  // no-op — consecutive load failures wouldn't change the persisted ref and
  // the panel would stay frozen forever. The counters always increment in
  // the loader's finally block. Waiting for BOTH avoids re-enabling editing
  // while one side is still mid-flight (which would risk a save that mixed
  // new-TF lows with stale highs).
  const [persistedFreshForTf, setPersistedFreshForTf] = useState(true);
  const lastTimeframeRef = useRef(resolvedTimeframe);
  const expectedLowGenRef = useRef(lowLoadGeneration);
  const expectedHighGenRef = useRef(highLoadGeneration);
  useEffect(() => {
    if (lastTimeframeRef.current !== resolvedTimeframe) {
      lastTimeframeRef.current = resolvedTimeframe;
      // Snapshot the current generations; both must advance past these
      // before we accept the new persisted values as fresh.
      expectedLowGenRef.current = lowLoadGeneration;
      expectedHighGenRef.current = highLoadGeneration;
      setPersistedFreshForTf(false);
    }
    // lowLoadGeneration and highLoadGeneration are intentionally omitted
    // from the dep array — we only want to snapshot them on TF change,
    // not on every counter bump.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedTimeframe]);
  useEffect(() => {
    if (
      lowLoadGeneration > expectedLowGenRef.current
      && highLoadGeneration > expectedHighGenRef.current
    ) {
      setPersistedFreshForTf(true);
    }
  }, [lowLoadGeneration, highLoadGeneration]);

  const handleHeaderPointerDown = useCallback((event) => {
    // Ignore clicks on buttons inside the header.
    if (event.target.closest('button')) {return;}
    const panel = event.currentTarget.closest('[data-cycle-tuning-panel]');
    if (!panel) {return;}
    const rect = panel.getBoundingClientRect();
    dragStateRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
    };
    event.preventDefault();
  }, []);

  useEffect(() => {
    const handleMove = (event) => {
      const drag = dragStateRef.current;
      if (!drag) {return;}
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - drag.width, event.clientX - drag.offsetX)),
        y: Math.max(0, event.clientY - drag.offsetY),
      });
    };
    const handleUp = () => { dragStateRef.current = null; };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, []);

  // Memoise so the deps of the dirty-check useMemos below are stable and
  // don't churn on every render when persisted/local refs are unchanged.
  const lowValues = useMemo(
    () => localLowSettings ?? persistedLowSettings ?? {},
    [localLowSettings, persistedLowSettings],
  );
  const highValues = useMemo(
    () => localHighSettings ?? persistedHighSettings ?? {},
    [localHighSettings, persistedHighSettings],
  );

  const isLowDirty = useMemo(
    () => localLowSettings
      && lowFields.some((f) => lowValues[f.field] !== (persistedLowSettings ?? {})[f.field]),
    [localLowSettings, lowValues, persistedLowSettings, lowFields],
  );
  const isHighDirty = useMemo(
    () => localHighSettings
      && highFields.some((f) => highValues[f.field] !== (persistedHighSettings ?? {})[f.field]),
    [localHighSettings, highValues, persistedHighSettings, highFields],
  );
  const isDirty = isLowDirty || isHighDirty;

  // Effective settings the chart is rendering NOW (locally-edited
  // values take precedence over saved values). Pass to the
  // forward-confidence hook so the displayed accuracy reflects what
  // the user is currently looking at — not what's saved to
  // SystemSettings yet. This is the whole point of the panel:
  // measure THIS tune before committing.
  const effectiveSettings = useMemo(() => ({
    low: lowValues,
    high: highValues,
  }), [lowValues, highValues]);

  const {
    data: confidenceData,
    loading: confidenceLoading,
    error: confidenceError,
  } = useForwardConfidence({
    symbol,
    timeframe: toCanonicalTimeframe(resolvedTimeframe),
    effectiveSettings,
    // Don't measure while persisted values from the previous TF are
    // still in `localXxxSettings` — that produces an apples-to-oranges
    // accuracy reading. Wait for the parent to load fresh persisted
    // values for the new TF before measuring.
    enabled: open && !!symbol && persistedFreshForTf,
  });

  const handleLowFieldChange = (field, rawValue) => {
    // No-op while we're showing stale persisted values from the previous
    // timeframe — the dirty check would seed local state from the wrong TF.
    if (!persistedFreshForTf) {return;}
    const parsed = rawValue === '' ? undefined : Number(rawValue);
    // Seed local state from persisted values on first edit so partial edits
    // don't silently blank unchanged fields.
    const base = localLowSettings ?? { ...(persistedLowSettings ?? {}) };
    onLocalLowChange({ ...base, [field]: parsed });
  };

  const handleHighFieldChange = (field, rawValue) => {
    if (!persistedFreshForTf) {return;}
    const parsed = rawValue === '' ? undefined : Number(rawValue);
    const base = localHighSettings ?? { ...(persistedHighSettings ?? {}) };
    onLocalHighChange({ ...base, [field]: parsed });
  };

  const handleDiscard = () => {
    onLocalLowChange(null);
    onLocalHighChange(null);
    setError(null);
  };

  const handleSave = async () => {
    // Defensive: the Save button is disabled when !symbol, but guard anyway.
    // Without this, `buildSettingKey(baseKey, undefined)` returns the bare
    // baseKey and we'd accidentally overwrite global settings instead of
    // writing a per-symbol override.
    if (!symbol) {return;}
    // Same defence for stale-persisted: if persisted hasn't refreshed for
    // the current timeframe yet, dirty values would be derived from the
    // previous TF's settings. Bail rather than write garbage.
    if (!persistedFreshForTf) {return;}
    setSaving(true);
    setError(null);
    try {
      const writes = [];
      // Per field: if the local value differs from the persisted one, either
      // write it (when finite) or delete the per-symbol override (when the
      // user cleared the field — they want to fall back to the global).
      // Ignoring cleared fields would let the Save button flash and do
      // nothing, leaving the old value in place on reload.
      const syncField = (field, localSettings, persistedSettings, legacyBaseKeys) => {
        const persistedValue = (persistedSettings ?? {})[field.field];
        const localValue = localSettings[field.field];
        if (localValue === persistedValue) {return;}
        const key = buildSettingKey(field.baseKey, symbol);
        // On weekly only: also delete the legacy (pre-Phase-2b) key on every
        // save or clear. Otherwise the library's weekly dual-read could
        // resurrect a pre-migration override after the user clears the new
        // entry. Daily/hourly never had a legacy schema so this is a no-op.
        const legacyKey = isWeekly && legacyBaseKeys
          ? buildSettingKey(legacyBaseKeys[field.field], symbol)
          : null;
        if (Number.isFinite(localValue)) {
          writes.push(updateSystemSetting(key, localValue));
          if (legacyKey) {
            writes.push(deleteSystemSetting(legacyKey).catch(() => null));
          }
        } else if (persistedValue !== undefined) {
          // Field was cleared; drop the override so the global / library
          // default wins next load. Legacy delete only fires on weekly.
          writes.push(deleteSystemSetting(key).catch(() => null));
          if (legacyKey) {
            writes.push(deleteSystemSetting(legacyKey).catch(() => null));
          }
        }
      };
      if (isLowDirty && localLowSettings) {
        lowFields.forEach(
          (f) => syncField(f, localLowSettings, persistedLowSettings, LEGACY_WEEKLY_CYCLE_LOW_BASE_KEYS),
        );
      }
      if (isHighDirty && localHighSettings) {
        highFields.forEach(
          (f) => syncField(f, localHighSettings, persistedHighSettings, LEGACY_WEEKLY_CYCLE_HIGH_BASE_KEYS),
        );
      }
      await Promise.all(writes);
      onLocalLowChange(null);
      onLocalHighChange(null);
      if (onSettingsChangedRemotely) {
        await onSettingsChangedRemotely();
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save cycle tuning');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToGlobal = async () => {
    // Wipes every per-symbol override key so detection falls back to globals.
    // Uses Promise.allSettled so partial failures don't leave local state in
    // an inconsistent place: we report a count back to the user rather than
    // silently succeeding when the server refused some/all deletes.
    if (!symbol) {return;}
    setResetting(true);
    setError(null);
    try {
      const primaryKeys = [
        ...lowFields.map((f) => buildSettingKey(f.baseKey, symbol)),
        ...highFields.map((f) => buildSettingKey(f.baseKey, symbol)),
      ];
      // Best-effort legacy wipe (weekly only) so dual-read can't resurrect a
      // pre-migration override for any field the user just reset. These may
      // 404 if the legacy keys never existed for this symbol — that's fine,
      // the promises are fire-and-forget and don't affect the success count.
      // Daily and hourly never had a legacy schema so we skip the wipe.
      if (isWeekly) {
        const legacyKeys = [
          ...lowFields.map(
            (f) => buildSettingKey(LEGACY_WEEKLY_CYCLE_LOW_BASE_KEYS[f.field], symbol),
          ),
          ...highFields.map(
            (f) => buildSettingKey(LEGACY_WEEKLY_CYCLE_HIGH_BASE_KEYS[f.field], symbol),
          ),
        ];
        legacyKeys.forEach((k) => {
          deleteSystemSetting(k).catch(() => null);
        });
      }
      const results = await Promise.allSettled(primaryKeys.map((k) => deleteSystemSetting(k)));
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length === primaryKeys.length) {
        // All deletes failed — keep local overrides so the user can retry.
        const first = failures[0]?.reason;
        setError(first?.response?.data?.message || first?.message || 'Failed to reset to global');
        return;
      }
      if (failures.length > 0) {
        // Partial failure — some overrides cleared, some remain. Still fall
        // through so the user sees the reload and can judge what stuck.
        setError(`Partially reset: ${primaryKeys.length - failures.length}/${primaryKeys.length} overrides cleared.`);
      }
      onLocalLowChange(null);
      onLocalHighChange(null);
      if (onSettingsChangedRemotely) {
        await onSettingsChangedRemotely();
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to reset to global');
    } finally {
      setResetting(false);
    }
  };

  const renderFields = (fields, values, onChange, persistedSide) => (
    <Stack spacing={1}>
      {fields.map((f) => {
        const persisted = persistedSide ?? {};
        const local = values[f.field];
        // `local !== persisted[f.field]` covers every case: fresh edits,
        // clearings (local === undefined, persisted numeric), and the
        // no-change path (lowValues falls back to persisted so they equal
        // and the row stays un-highlighted).
        const isFieldDirty = local !== persisted[f.field];
        return (
          <Tooltip key={f.field} title={f.helperText} placement="left" enterDelay={300}>
            <TextField
              label={f.label}
              type="number"
              value={local ?? ''}
              onChange={(e) => onChange(f.field, e.target.value)}
              size="small"
              inputProps={f.inputProps}
              disabled={!persistedFreshForTf}
              sx={{
                '& .MuiInputBase-root': isFieldDirty ? { bgcolor: 'rgba(250, 204, 21, 0.12)' } : {},
              }}
              fullWidth
            />
          </Tooltip>
        );
      })}
    </Stack>
  );

  if (!open) {return null;}

  // Default to top-right with 16px margin once the viewport is available.
  const resolvedX = position.x !== null && position.x !== undefined
    ? position.x
    : Math.max(0, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 720);

  return (
    <Paper
      data-cycle-tuning-panel
      elevation={8}
      sx={{
        position: 'fixed',
        top: position.y,
        left: resolvedX,
        // Width halves when only one side is visible, since the grid is then
        // a single column and the full-width layout would leave awkward empty
        // space. xs stays viewport-wide either way.
        width: {
          xs: 'calc(100vw - 32px)',
          sm: (showLowsSection && showHighsSection) ? 560 : 320,
          md: (showLowsSection && showHighsSection) ? 700 : 380,
        },
        maxHeight: 'calc(100vh - 32px)',
        overflow: 'auto',
        zIndex: 1300,
        p: 2,
        // Semi-transparent so chart markers underneath remain partly visible
        // while the panel is open. Backdrop-blur keeps text legible against
        // whatever's behind. Sits over a hint of the app's paper colour so
        // the panel doesn't look like a floating pile of text on dark charts.
        backgroundColor: 'rgb(var(--dwlf-bg-rgb) / 0.8)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <Box
        onPointerDown={handleHeaderPointerDown}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'grab',
          touchAction: 'none',
          userSelect: 'none',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="subtitle1" sx={{ flex: 1 }}>
          Cycle Tuning — {symbol || '(no symbol)'} {resolvedTimeframe}
          {!persistedFreshForTf && (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>• loading…</Typography>
          )}
          {isDirty && <Typography component="span" variant="caption" sx={{ ml: 1, color: 'warning.main' }}>• unsaved</Typography>}
        </Typography>
        <IconButton
          size="small"
          title="Reset this symbol's overrides to global defaults"
          onClick={handleResetToGlobal}
          disabled={!symbol || resetting || saving}
        >
          <RestartAltIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={onClose} title="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {(() => {
        // Forward-confidence indicator. The shape of "this tune
        // produces N% correct historical cycle highs/lows" is the
        // missing piece of the trust loop — without a number, every
        // tune is gut-feel. Render below the panel header so users
        // see it update when they change a value.
        const label = formatAccuracyLabel({
          data: confidenceData,
          loading: confidenceLoading,
          error: confidenceError,
        });
        if (!label) {return null;}
        const outcome = confidenceData?.outcome;
        const outcomeText = outcome
          ? `(price moves ≥${outcome.movePct}% within ${outcome.windowBars} bars)`
          : null;
        return (
          <Box sx={{
            mt: 1, mb: 0.5, p: 0.75, borderRadius: 1,
            backgroundColor: 'var(--dwlf-ink-04)',
            display: 'flex', alignItems: 'baseline', gap: 1,
          }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              Historical accuracy:
            </Typography>
            <Typography variant="caption" sx={{ color: label.color, fontWeight: 500 }}>
              {label.text}
            </Typography>
            {outcomeText && (
              <Typography variant="caption" sx={{ color: 'text.disabled', ml: 'auto' }}>
                {outcomeText}
              </Typography>
            )}
          </Box>
        );
      })()}


      <Box>
        <Divider sx={{ my: 1 }} />
        <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
          <Chip
            label="Lows"
            size="small"
            variant={showLowsSection ? 'filled' : 'outlined'}
            onClick={() => setShowLowsSection((v) => !v)}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          <Chip
            label="Highs"
            size="small"
            variant={showHighsSection ? 'filled' : 'outlined'}
            onClick={() => setShowHighsSection((v) => !v)}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Stack>
        {error && (
          <Typography variant="caption" color="error.main" sx={{ mb: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: (showLowsSection && showHighsSection) ? '1fr 1fr' : '1fr',
            },
            gap: 2,
          }}
        >
          {showLowsSection && (
            <Box>
              <Typography variant="overline" color="text.secondary">Lows</Typography>
              {renderFields(lowFields, lowValues, handleLowFieldChange, persistedLowSettings)}
            </Box>
          )}
          {showHighsSection && (
            <Box>
              <Typography variant="overline" color="text.secondary">Highs</Typography>
              {renderFields(highFields, highValues, handleHighFieldChange, persistedHighSettings)}
            </Box>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            onClick={handleDiscard}
            disabled={!isDirty || saving}
          >
            Discard
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={!isDirty || saving || !symbol || !persistedFreshForTf}
          >
            {saving ? 'Saving…' : 'Save overrides'}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
};

export default CycleTuningPanel;
